import {createAsyncThunk} from '@reduxjs/toolkit'
import {
  FetchInventoryItemByIdRequest,
  UpdateInventoryItemRequest,
  FetchInventoryItemsRequest,
  CreateInventoryItemRequest,
  DeleteInventoryItemRequest,
  AssignInventoryItemRequest,
  UnassignInventoryItemRequest,
  UpdateMaintenanceRequest,
  FetchAllInventoryItemsRequest,
} from './types'
import {AxiosError} from 'axios'
import {ResponseError} from '../commonTypes'
import {InventoryItem} from '../../../models'
import axios from 'axios'
import {setupAxios} from '../../setup-axios'
import {
  inventoryUrl,
  createInventoryUrl,
  updateInventoryUrl,
  deleteInventoryUrl,
  assignInventoryUrl,
  unassignInventoryUrl,
  maintenanceInventoryUrl,
} from '../../api'

// Using centralized API URLs
const fetchAllInventoryItemsUrl = `${inventoryUrl}/all`

export const fetchInventoryItems = createAsyncThunk(
  'inventory/fetch',
  async (payload: FetchInventoryItemsRequest, {rejectWithValue}) => {
    try {
      setupAxios(axios)
      // Add simple cache-busting parameter to prevent stale data
      const cacheBuster = payload.timestamp ? `&_t=${payload.timestamp}` : `&_t=${Date.now()}`
      
      
      // For admin users (role=2), don't pass role parameter to see all items
      const roleParam = payload.role === '2' ? '' : `&role=${payload.role}`
      
      // Build query parameters properly
      const searchParam = payload.search && payload.search.trim() !== '' ? `&search=${encodeURIComponent(payload.search.trim())}` : ''
      const categoryParam = payload.category ? `&category=${encodeURIComponent(payload.category)}` : ''
      const statusParam = payload.status ? `&status=${encodeURIComponent(payload.status)}` : ''
      const conditionParam = payload.condition ? `&condition=${encodeURIComponent(payload.condition)}` : ''
      const assignedToParam = payload.assignedTo ? `&assignedTo=${encodeURIComponent(payload.assignedTo)}` : ''
      const establishementParam = payload.establishement ? `&establishement=${encodeURIComponent(payload.establishement)}` : ''
      
      const url = `${inventoryUrl}?sortField=${payload.sortField}&sortOrder=${payload.sortOrder}&pageNumber=${payload.pageNumber}&pageCount=${payload.pageCount}${searchParam}${categoryParam}${statusParam}${conditionParam}${assignedToParam}${establishementParam}${roleParam}${cacheBuster}`
      
      console.log('🔍 Fetching inventory with URL:', url)
      console.log('🔍 Search parameters:', {
        search: payload.search,
        category: payload.category,
        status: payload.status,
        condition: payload.condition,
        establishement: payload.establishement
      })
      
      const res = await axios.get(url)
      const {inventoryItems, totalPages, total} = res.data
      return {
        inventoryItems,
        totalPages,
        total,
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const errorMessage = err.response?.data?.message || 'Failed to fetch inventory items'
      return rejectWithValue({error: errorMessage, message: errorMessage, success: false} as ResponseError)
    }
  }
)

export const fetchAllInventoryItems = createAsyncThunk(
  'inventory/fetchAll',
  async (payload: FetchAllInventoryItemsRequest, {rejectWithValue}) => {
    try {
      setupAxios(axios)
      const res = await axios.get(fetchAllInventoryItemsUrl)
      const {inventoryItems} = res.data
      return {
        inventoryItems,
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const errorMessage = err.response?.data?.message || 'Failed to fetch all inventory items'
      return rejectWithValue({error: errorMessage, message: errorMessage, success: false} as ResponseError)
    }
  }
)

export const fetchInventoryItemById = createAsyncThunk(
  'inventory/fetchById',
  async (payload: FetchInventoryItemByIdRequest, {rejectWithValue}) => {
    try {
      setupAxios(axios)
      const res = await axios.get(`${inventoryUrl}/${payload.id}`)
      const {inventoryItem} = res.data
      return {
        inventoryItem,
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const errorMessage = err.response?.data?.message || 'Failed to fetch inventory item'
      return rejectWithValue({error: errorMessage, message: errorMessage, success: false} as ResponseError)
    }
  }
)

export const createInventoryItem = createAsyncThunk(
  'inventory/create',
  async (payload: CreateInventoryItemRequest, {rejectWithValue, dispatch}) => {
    try {
      setupAxios(axios)
      
      // Check if there are attachments (files) to upload
      const hasAttachments = payload.attachments && payload.attachments.some((file: any) => file instanceof File)
      
      if (hasAttachments) {
        // Use FormData for file uploads
        const formData = new FormData()
        
        // Append all regular fields
        formData.append('name', payload.name)
        if (payload.description) formData.append('description', payload.description)
        formData.append('category', payload.category)
        if (payload.inventoryType) formData.append('inventoryType', payload.inventoryType)
        if (payload.sku) formData.append('sku', payload.sku)
        if (payload.size) formData.append('size', payload.size)
        if (payload.brand) formData.append('brand', payload.brand)
        if (payload.model) formData.append('model', payload.model)
        formData.append('quantityOnHand', payload.quantityOnHand.toString())
        formData.append('quantityOnOrder', payload.quantityOnOrder.toString())
        formData.append('quantityReserved', payload.quantityReserved.toString())
        formData.append('unitCost', payload.unitCost.toString())
        if (payload.totalValue !== undefined) formData.append('totalValue', payload.totalValue.toString())
        formData.append('condition', payload.condition)
        formData.append('status', payload.status)
        formData.append('equipmentStatus', payload.equipmentStatus)
        if (payload.assignedTo) formData.append('assignedTo', payload.assignedTo)
        if (payload.assignedDate) formData.append('assignedDate', payload.assignedDate)
        if (payload.lastMaintenanceDate) formData.append('lastMaintenanceDate', payload.lastMaintenanceDate)
        if (payload.nextMaintenanceDate) formData.append('nextMaintenanceDate', payload.nextMaintenanceDate)
        if (payload.maintenanceNotes) formData.append('maintenanceNotes', payload.maintenanceNotes)
        if (payload.supplier) formData.append('supplier', payload.supplier)
        if (payload.supplierContact) formData.append('supplierContact', payload.supplierContact)
        if (payload.supplierEmail) formData.append('supplierEmail', payload.supplierEmail)
        if (payload.supplierPhone) formData.append('supplierPhone', payload.supplierPhone)
        if (payload.orderDate) formData.append('orderDate', payload.orderDate)
        if (payload.expectedDeliveryDate) formData.append('expectedDeliveryDate', payload.expectedDeliveryDate)
        if (payload.receivedDate) formData.append('receivedDate', payload.receivedDate)
        formData.append('minimumStockLevel', payload.minimumStockLevel.toString())
        formData.append('maximumStockLevel', payload.maximumStockLevel.toString())
        formData.append('reorderPoint', payload.reorderPoint.toString())
        formData.append('reorderQuantity', payload.reorderQuantity.toString())
        if (payload.notes) formData.append('notes', payload.notes)
        formData.append('establishement', payload.establishement)
        if (payload.isActive !== undefined) formData.append('isActive', payload.isActive.toString())
        
        // Append file attachments
        if (payload.attachments) {
          payload.attachments.forEach((file: any) => {
            if (file instanceof File) {
              formData.append('files', file)
            }
          })
        }
        
        const res = await axios.post(createInventoryUrl, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const {loaded, total} = progressEvent
            if (total) {
              const percentCompleted = Math.round((loaded * 100) / total)
              console.log(`Upload progress: ${percentCompleted}%`)
              // You can dispatch progress updates if needed
            }
          },
        })
        
        console.log('createInventoryItem API response:', res.data)
        const {inventoryItem} = res.data
        return {
          inventoryItem,
          success: true,
        }
      } else {
        // No attachments, use regular JSON request
        const res = await axios.post(createInventoryUrl, payload)
        console.log('createInventoryItem API response:', res.data)
        const {inventoryItem} = res.data
        return {
          inventoryItem,
          success: true,
        }
      }
    } catch (error) {
      console.error('createInventoryItem API error:', error)
      const err: any = error as AxiosError
      const errorMessage = err.response?.data?.message || 'Failed to create inventory item'
      return rejectWithValue({error: errorMessage, message: errorMessage, success: false} as ResponseError)
    }
  }
)

export const updateInventoryItem = createAsyncThunk(
  'inventory/update',
  async (payload: UpdateInventoryItemRequest, {rejectWithValue}) => {
    try {
      console.log('updateInventoryItem API call with payload:', payload)
      setupAxios(axios)
      
      // Check if there are new attachments (files) to upload
      const hasNewAttachments = payload.attachments && payload.attachments.some((file: any) => file instanceof File)
      
      if (hasNewAttachments || payload.deletedAttachments) {
        // Use FormData for file uploads or deletions
        const formData = new FormData()
        
        // Append ID (required for update)
        formData.append('id', payload.id)
        
        // Append all regular fields
        formData.append('name', payload.name)
        if (payload.description) formData.append('description', payload.description)
        formData.append('category', payload.category)
        if (payload.inventoryType) formData.append('inventoryType', payload.inventoryType)
        if (payload.sku) formData.append('sku', payload.sku)
        if (payload.size) formData.append('size', payload.size)
        if (payload.brand) formData.append('brand', payload.brand)
        if (payload.model) formData.append('model', payload.model)
        formData.append('quantityOnHand', payload.quantityOnHand.toString())
        formData.append('quantityOnOrder', payload.quantityOnOrder.toString())
        formData.append('quantityReserved', payload.quantityReserved.toString())
        formData.append('unitCost', payload.unitCost.toString())
        if (payload.totalValue !== undefined) formData.append('totalValue', payload.totalValue.toString())
        formData.append('condition', payload.condition)
        formData.append('status', payload.status)
        formData.append('equipmentStatus', payload.equipmentStatus)
        if (payload.assignedTo) formData.append('assignedTo', payload.assignedTo)
        if (payload.assignedDate) formData.append('assignedDate', payload.assignedDate)
        if (payload.lastMaintenanceDate) formData.append('lastMaintenanceDate', payload.lastMaintenanceDate)
        if (payload.nextMaintenanceDate) formData.append('nextMaintenanceDate', payload.nextMaintenanceDate)
        if (payload.maintenanceNotes) formData.append('maintenanceNotes', payload.maintenanceNotes)
        if (payload.supplier) formData.append('supplier', payload.supplier)
        if (payload.supplierContact) formData.append('supplierContact', payload.supplierContact)
        if (payload.supplierEmail) formData.append('supplierEmail', payload.supplierEmail)
        if (payload.supplierPhone) formData.append('supplierPhone', payload.supplierPhone)
        if (payload.orderDate) formData.append('orderDate', payload.orderDate)
        if (payload.expectedDeliveryDate) formData.append('expectedDeliveryDate', payload.expectedDeliveryDate)
        if (payload.receivedDate) formData.append('receivedDate', payload.receivedDate)
        formData.append('minimumStockLevel', payload.minimumStockLevel.toString())
        formData.append('maximumStockLevel', payload.maximumStockLevel.toString())
        formData.append('reorderPoint', payload.reorderPoint.toString())
        formData.append('reorderQuantity', payload.reorderQuantity.toString())
        if (payload.notes) formData.append('notes', payload.notes)
        formData.append('establishement', payload.establishement)
        if (payload.isActive !== undefined) formData.append('isActive', payload.isActive.toString())
        
        // Append new file attachments
        if (payload.attachments) {
          payload.attachments.forEach((file: any) => {
            if (file instanceof File) {
              formData.append('files', file)
            }
          })
        }
        
        // Append deleted attachments list
        if (payload.deletedAttachments && payload.deletedAttachments.length > 0) {
          formData.append('deletedAttachments', JSON.stringify(payload.deletedAttachments))
        }
        
        const res = await axios.post(updateInventoryUrl, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const {loaded, total} = progressEvent
            if (total) {
              const percentCompleted = Math.round((loaded * 100) / total)
              console.log(`Upload progress: ${percentCompleted}%`)
            }
          },
        })
        
        console.log('updateInventoryItem API response:', res.data)
        const {inventoryItem} = res.data
        return {
          inventoryItem,
          success: true,
        }
      } else {
        // No attachments, use regular JSON request
        const res = await axios.post(updateInventoryUrl, payload)
        console.log('updateInventoryItem API response:', res.data)
        const {inventoryItem} = res.data
        return {
          inventoryItem,
          success: true,
        }
      }
    } catch (error) {
      console.error('updateInventoryItem API error:', error)
      const err: any = error as AxiosError
      const errorMessage = err.response?.data?.message || 'Failed to update inventory item'
      return rejectWithValue({error: errorMessage, message: errorMessage, success: false} as ResponseError)
    }
  }
)

export const deleteInventoryItem = createAsyncThunk(
  'inventory/delete',
  async (payload: DeleteInventoryItemRequest, {rejectWithValue}) => {
    try {
      setupAxios(axios)
      const res = await axios.post(deleteInventoryUrl, payload)
      const {inventoryItem} = res.data
      return {
        inventoryItem,
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const errorMessage = err.response?.data?.message || 'Failed to delete inventory item'
      return rejectWithValue({error: errorMessage, message: errorMessage, success: false} as ResponseError)
    }
  }
)

export const assignInventoryItem = createAsyncThunk(
  'inventory/assign',
  async (payload: AssignInventoryItemRequest, {rejectWithValue}) => {
    try {
      setupAxios(axios)
      const res = await axios.post(assignInventoryUrl, {
        id: payload.id,
        assignedTo: payload.assignedTo,
        assignedDate: payload.assignedDate,
        notes: payload.notes,
      })
      const {inventoryItem} = res.data
      return {
        inventoryItem,
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const errorMessage = err.response?.data?.message || 'Failed to assign inventory item'
      return rejectWithValue({error: errorMessage, message: errorMessage, success: false} as ResponseError)
    }
  }
)

export const unassignInventoryItem = createAsyncThunk(
  'inventory/unassign',
  async (payload: UnassignInventoryItemRequest, {rejectWithValue}) => {
    try {
      setupAxios(axios)
      const res = await axios.post(unassignInventoryUrl, {
        id: payload.id,
        notes: payload.notes,
      })
      const {inventoryItem} = res.data
      return {
        inventoryItem,
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const errorMessage = err.response?.data?.message || 'Failed to unassign inventory item'
      return rejectWithValue({error: errorMessage, message: errorMessage, success: false} as ResponseError)
    }
  }
)

export const updateMaintenance = createAsyncThunk(
  'inventory/updateMaintenance',
  async (payload: UpdateMaintenanceRequest, {rejectWithValue}) => {
    try {
      setupAxios(axios)
      const res = await axios.post(maintenanceInventoryUrl, {
        id: payload.id,
        lastMaintenanceDate: payload.lastMaintenanceDate,
        nextMaintenanceDate: payload.nextMaintenanceDate,
        maintenanceNotes: payload.maintenanceNotes,
        condition: payload.condition,
      })
      const {inventoryItem} = res.data
      return {
        inventoryItem,
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const errorMessage = err.response?.data?.message || 'Failed to update maintenance'
      return rejectWithValue({error: errorMessage, message: errorMessage, success: false} as ResponseError)
    }
  }
)
